import { ErrorInterface } from 'modules/core/api/globalApi';
import { isDev } from './functions';

/**
 * Парсит "сырой" ответ от сервера (любого формата) в типизированный ErrorInterface.
 * Обрабатывает: JSON, HTML-страницы ошибок Spring Boot, plain text, Network errors.
 */
export function parseApiError(rawError: unknown, statusCode?: number): ErrorInterface {
  // 1. Сетевые ошибки (fetch/axios без ответа)
  if (rawError instanceof TypeError && rawError.message.includes('Failed to fetch')) {
    return {
      message: 'Нет соединения с сервером',
      errorCode: 'NETWORK_ERROR',
      status: 0,
      connectError: true,
      isError: true,
    };
  }

  // 2. Обработка объекта
  if (typeof rawError === 'object' && rawError !== null) {
    const obj = rawError as Record<string, unknown>;
    
    // Проверяем, есть ли поле message и не содержит ли оно HTML
    let message = '';
    if ('message' in obj && typeof obj.message === 'string') {
      const rawMessage = obj.message as string;
      // Если message содержит HTML — извлекаем текст
      message = isHtmlString(rawMessage) 
        ? extractTextFromHtml(rawMessage) || rawMessage 
        : rawMessage;
    }
    
    // Если message пустой, пробуем другие поля
    if (!message) {
      message = String(obj.message ?? obj.error ?? obj.errorMessage ?? obj.detail ?? 'Ошибка сервера');
    }
    
    // Если всё ещё HTML (на всякий случай)
    if (isHtmlString(message)) {
      message = extractTextFromHtml(message) || message;
    }

    // Формируем errorCode
    const errorCode = String(
      obj.errorCode ?? 
      obj.code ?? 
      obj.status ?? 
      (statusCode?.toString() ?? 'UNKNOWN_ERROR')
    );

    return {
      message: message.trim(),
      errorCode,
      status: Number(obj.status ?? statusCode) || undefined,
      isError: true,
      // Dev-поля: копируем только если включён dev-режим
      ...(isDev && {
        details: typeof obj.details === 'string' ? obj.details : undefined,
        timestamp: typeof obj.timestamp === 'string' ? obj.timestamp : undefined,
        exception: typeof obj.exception === 'string' ? obj.exception : undefined,
        stackTrace: typeof obj.stackTrace === 'string' ? obj.stackTrace : undefined,
      }),
      // Всегда копируем id, если есть
      ...(obj.id && { id: String(obj.id) }),
    };
  }

  // 3. Обработка строки (возможно, это текст ошибки или не спаршенный JSON)
  if (typeof rawError === 'string') {
    // Пробуем распарсить как JSON
    try {
      const parsed = JSON.parse(rawError);
      return parseApiError(parsed, statusCode); // Рекурсивный вызов для объекта
    } catch {
      // Не JSON, значит HTML или plain text
      const message = isHtmlString(rawError)
        ? extractTextFromHtml(rawError) || 'Неизвестная ошибка сервера'
        : rawError;
      
      return {
        message: message.trim(),
        errorCode: 'SERVER_ERROR',
        status: statusCode,
        isError: true,
        ...(isDev && { details: rawError }),
      };
    }
  }

  // 4. Fallback для любых других случаев
  return {
    message: String(rawError ?? 'Неизвестная ошибка'),
    errorCode: 'UNKNOWN_ERROR',
    status: statusCode,
    isError: true,
  };
}

// ─── Вспомогательные функции ───────────────────────────────────────────────

/**
 * Проверяет, является ли строка HTML
 */
function isHtmlString(str: string): boolean {
  if (typeof str !== 'string' || str.length < 10) {
    return false;
  }
  
  // Проверяем наличие характерных HTML-тегов
  const htmlPattern = /<\s*(html|head|body|title|div|p|h[1-6])[\s>]/i;
  return htmlPattern.test(str);
}

/**
 * Извлекает читаемый текст из HTML-страницы ошибки (например, от Spring Boot).
 */
function extractTextFromHtml(html: string): string | null {
  if (!isHtmlString(html)) {
    return html.trim() || null;
  }

  try {
    if (typeof document !== 'undefined' && 'DOMParser' in window) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Проверяем на ошибки парсинга
      const parserError = doc.querySelector('parsererror');
      if (parserError) {
        return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || null;
      }
      
      // Пробуем извлечь текст из body
      const bodyText = doc.body?.textContent?.trim();
      if (bodyText && bodyText.length > 0 && bodyText.length < 500) {
        return bodyText;
      }
      
      // Пробуем найти <title>, но игнорируем общий "Error"
      const title = doc.title?.trim();
      if (title && title.toLowerCase() !== 'error') {
        return title;
      }
    }
    
    // Fallback: убираем теги регекспом
    const stripped = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return stripped || null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[parseApiError] Failed to parse HTML:', e);
    return null;
  }
}